import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Heart, MapPin, ShoppingBag, Clock } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import axios from 'axios';

const API_URL = '';

const CustomerDashboard = () => {
    const { user } = useUser();
    const [metrics, setMetrics] = useState({
        totalOrders: 0,
        wishlistCount: 0,
        savedAddresses: 0
    });
    const [recentOrder, setRecentOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            let fetchedOrders = [];
            try {
                let token = localStorage.getItem('token') || sessionStorage.getItem('token');
                if (!token) {
                    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                    token = userInfo.token;
                }
                
                if (token) {
                    const res = await axios.get(`${API_URL}/api/orders/myorders`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data.success) {
                        fetchedOrders = res.data.orders || [];
                    }
                }
            } catch (error) {
                console.error("Error fetching orders:", error);
            }

            const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
            const addresses = JSON.parse(localStorage.getItem('customerAddresses') || '[]');

            setMetrics({
                totalOrders: fetchedOrders.length,
                wishlistCount: wishlist.length,
                savedAddresses: addresses.length
            });

            if (fetchedOrders.length > 0) {
                setRecentOrder(fetchedOrders[0]);
            }

            setLoading(false);
        };
        
        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="customer-dashboard">
            {/* Welcome */}
            <div className="welcome-box">
                <h2>Hello, {user?.username || 'Customer'}! 👋</h2>
                <p>Welcome to your account</p>
            </div>

            {/* Stats - 3 cards only */}
            <div className="stats-row">
                <Link to="/account/orders" className="stat-box">
                    <Package size={22} />
                    <div>
                        <strong>{metrics.totalOrders}</strong>
                        <span>Orders</span>
                    </div>
                </Link>

                <Link to="/account/wishlist" className="stat-box">
                    <Heart size={22} />
                    <div>
                        <strong>{metrics.wishlistCount}</strong>
                        <span>Wishlist</span>
                    </div>
                </Link>

                <Link to="/account/address" className="stat-box">
                    <MapPin size={22} />
                    <div>
                        <strong>{metrics.savedAddresses}</strong>
                        <span>Addresses</span>
                    </div>
                </Link>
            </div>

            {/* Recent Order - if exists */}
            {recentOrder && (
                <div className="recent-box">
                    <div className="recent-header">
                        <ShoppingBag size={18} />
                        <h3>Recent Order</h3>
                        <Link to="/account/orders">View all →</Link>
                    </div>
                    <div className="recent-body">
                        <div className="order-row">
                            <span className="order-label">Order ID</span>
                            <span>#{recentOrder._id?.slice(-8).toUpperCase()}</span>
                        </div>
                        <div className="order-row">
                            <span className="order-label">Date</span>
                            <span>{new Date(recentOrder.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="order-row">
                            <span className="order-label">Status</span>
                            <span className={`status-${(recentOrder.status || 'Processing').toLowerCase()}`}>
                                {recentOrder.status || 'Processing'}
                            </span>
                        </div>
                        <div className="order-row">
                            <span className="order-label">Total</span>
                            <span className="order-amount">₹{recentOrder.totalAmount?.toLocaleString() || '0'}</span>
                        </div>
                        {recentOrder.orderItems?.[0] && (
                            <div className="order-product">
                                <img 
                                    src={recentOrder.orderItems[0].product?.image || '/assets/images/thumbs/plant-placeholder.png'} 
                                    alt=""
                                />
                                <span>{recentOrder.orderItems[0].name}</span>
                                {recentOrder.orderItems.length > 1 && (
                                    <span className="more-items">+{recentOrder.orderItems.length - 1} more</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* No Orders State */}
            {metrics.totalOrders === 0 && (
                <div className="empty-box">
                    <Package size={48} />
                    <h3>No orders yet</h3>
                    <p>Start shopping to see your orders here</p>
                    <Link to="/shop" className="shop-link">Shop Now →</Link>
                </div>
            )}

            <style>{`
                .customer-dashboard {
                    max-width: 100%;
                }

                .dashboard-loading {
                    display: flex;
                    justify-content: center;
                    padding: 40px;
                }

                .loader {
                    width: 32px;
                    height: 32px;
                    border: 2px solid #e2e8f0;
                    border-top-color: #10b981;
                    border-radius: 50%;
                    animation: spin 0.6s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Welcome Box */
                .welcome-box {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    margin-bottom: 20px;
                    border: 1px solid #e8ecef;
                }

                .welcome-box h2 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 4px;
                }

                .welcome-box p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                }

                /* Stats Row - 3 cards */
                .stats-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .stat-box {
                    background: white;
                    border-radius: 16px;
                    padding: 18px;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    text-decoration: none;
                    border: 1px solid #e8ecef;
                    transition: all 0.2s;
                }

                .stat-box:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }

                .stat-box svg {
                    color: #10b981;
                }

                .stat-box strong {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                    display: block;
                    line-height: 1.2;
                }

                .stat-box span {
                    font-size: 12px;
                    color: #64748b;
                }

                /* Recent Order Box */
                .recent-box {
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8ecef;
                    overflow: hidden;
                    margin-bottom: 20px;
                }

                .recent-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e8ecef;
                }

                .recent-header svg {
                    color: #10b981;
                }

                .recent-header h3 {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                    flex: 1;
                }

                .recent-header a {
                    font-size: 12px;
                    color: #10b981;
                    text-decoration: none;
                }

                .recent-body {
                    padding: 16px 20px;
                }

                .order-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px solid #f1f5f9;
                }

                .order-row:last-of-type {
                    border-bottom: none;
                }

                .order-label {
                    font-size: 13px;
                    color: #64748b;
                }

                .order-row span:last-child {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1e293b;
                }

                .status-processing {
                    color: #f59e0b;
                }

                .status-shipped {
                    color: #3b82f6;
                }

                .status-delivered {
                    color: #10b981;
                }

                .order-amount {
                    font-weight: 700 !important;
                    color: #10b981 !important;
                }

                .order-product {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 1px solid #f1f5f9;
                }

                .order-product img {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    object-fit: cover;
                }

                .order-product span {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1e293b;
                }

                .more-items {
                    font-size: 11px !important;
                    color: #64748b !important;
                    font-weight: normal !important;
                    margin-left: auto;
                }

                /* Empty State */
                .empty-box {
                    background: white;
                    border-radius: 16px;
                    padding: 40px 24px;
                    text-align: center;
                    border: 1px solid #e8ecef;
                }

                .empty-box svg {
                    color: #94a3b8;
                    margin-bottom: 16px;
                }

                .empty-box h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 6px;
                }

                .empty-box p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0 0 20px;
                }

                .shop-link {
                    display: inline-block;
                    padding: 10px 24px;
                    background: #10b981;
                    color: white;
                    border-radius: 40px;
                    text-decoration: none;
                    font-size: 13px;
                    font-weight: 500;
                }

                /* Responsive */
                @media (max-width: 600px) {
                    .stats-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .welcome-box {
                        padding: 20px;
                    }
                    
                    .welcome-box h2 {
                        font-size: 18px;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerDashboard;